import * as React from "react";
import {useState} from "react";
import {useMutation} from "@apollo/client";
import {Badge, Button, Group, List, Stack, Table, Text} from "@mantine/core";
import {DatePickerInput} from "@mantine/dates";
import {IconCalendar, IconShare3, IconTrash} from "@tabler/icons-react";
import dayjs from "dayjs";

import {SomaFeaturesShape, SpecimenShape} from "../../models/specimen";
import {InputPopup} from "../common/InputPopup";
import {displayInjection, InjectionShape} from "../../models/injection";
import {CollectionSelect} from "../common/CollectionSelect";
import {GenotypeAutosuggest} from "../common/GenotypeAutosuggset";
import {UPDATE_SPECIMEN_MUTATION, UpdateSpecimenMutationResponse, UpdateSpecimenVariables} from "../../graphql/specimen";
import {toastUpdateError} from "../common/NotificationHelper";
import {Link} from "react-router-dom";
import {ReferenceDatasetModal} from "./ReferenceDatasetModal";
import {SomaFeaturesModal} from "./SomaFeaturesModal";
import {TomographyModal} from "./TomographyModal";
import {useConstants} from "../../hooks/useConstants";

export type SpecimenRowProps = {
    specimen: SpecimenShape;

    manageInjections(specimen: SpecimenShape): void;
    requestDelete(specimen: SpecimenShape): void;
}

export const SpecimenRow = ({specimen, manageInjections, requestDelete}: SpecimenRowProps) => {
    const constants = useConstants();

    const [isRefDataSetModalOpen, setIsRefDataSetModalOpen] = useState(false);
    const [isSomaModalOpen, setIsSomaModalOpen] = useState(false);
    const [isTomographyModalOpen, setIsTomographyModalOpen] = useState(false);

    const [updateSpecimen] = useMutation<UpdateSpecimenMutationResponse, UpdateSpecimenVariables>(UPDATE_SPECIMEN_MUTATION,
        {
            onError: (error) => toastUpdateError(error)
        });

    const updateLabel = async (value: string) => {
        if (value !== specimen.label) {
            await updateSpecimen({variables: {specimen: {id: specimen.id, label: value}}});
        }
    }

    const updateDate = async (s: string) => {
        const value = dayjs(s).toDate();
        if (value !== specimen.referenceDate) {
            await updateSpecimen({variables: {specimen: {id: specimen.id, referenceDate: value ? value.valueOf() : null}}});
        }
    }

    const updateNotes = async (value: string) => {
        if (value !== specimen.notes) {
            await updateSpecimen({variables: {specimen: {id: specimen.id, notes: value}}});
        }
    }

    const updateGenotype = async (name: string) => {
        if ((!specimen.genotype || name !== specimen.genotype.name) || (!name && specimen.genotype)) {
            await updateSpecimen({variables: {specimen: {id: specimen.id, genotypeName: name || null}}});
        }
    }

    const updateCollection = async (collectionId: string) => {
        if (collectionId !== specimen.collection?.id) {
            await updateSpecimen({variables: {specimen: {id: specimen.id, collectionId: collectionId}}});
        }
    }

    const refDataSetLabel = specimen.referenceDataset?.url
        ? <Text size="sm" style={{cursor: "pointer"}} onClick={() => setIsRefDataSetModalOpen(true)}>
            {specimen.referenceDataset.url.length > 20 ? specimen.referenceDataset.url.substring(0, 20) + "..." : specimen.referenceDataset.url}
          </Text>
        : <Text size="sm" c="dimmed" style={{cursor: "pointer"}} onClick={() => setIsRefDataSetModalOpen(true)}>click to edit</Text>;

    const tomographyLabel = specimen.tomography?.url
        ? <Text size="sm" style={{cursor: "pointer"}} onClick={() => setIsTomographyModalOpen(true)}>
            {specimen.tomography.url.length > 20 ? specimen.tomography.url.substring(0, 20) + "..." : specimen.tomography.url}
          </Text>
        : <Text size="sm" c="dimmed" style={{cursor: "pointer"}} onClick={() => setIsTomographyModalOpen(true)}>click to edit</Text>;

    const features = specimen.somaProperties
        ? <a style={{cursor: "pointer"}} onClick={() => setIsSomaModalOpen(true)}>{SomaPropertiesDisplay(specimen.somaProperties)}</a>
        : <Text size="sm" c="dimmed" style={{cursor: "pointer"}} onClick={() => setIsSomaModalOpen(true)}>click to edit</Text>;

    return (
        <Table.Tr key={specimen.id}>
            <Table.Td>
                <Group align="center" gap="sm">
                    <InputPopup value={specimen.label} label="Specimen Id" placeholder="Enter specimen id..." onAccept={updateLabel}/>
                    <Link to={`/specimen/${specimen.id}`}><IconShare3 style={{marginTop: "4px"}} size={18}/></Link>
                </Group>
            </Table.Td>
            <Table.Td>
                <DatePickerInput size="sm" leftSection={<IconCalendar size={18} stroke={1.5}/>} clearable placeholder="Select date" valueFormat="YYYY-MM-DD"
                                 value={specimen.referenceDate} type="default" onChange={updateDate}/>
            </Table.Td>
            <Table.Td>
                <InputPopup value={specimen.notes} label="Notes" placeholder="Enter notes..." onAccept={updateNotes}/>
            </Table.Td>
            <Table.Td>
                <GenotypeAutosuggest value={specimen.genotype ? specimen.genotype.name : ""} onChange={updateGenotype}/>
            </Table.Td>
            <Table.Td>
                <a onClick={() => manageInjections(specimen)}>
                    <InjectionList injections={specimen.injections} isExpanded={true}/>
                </a>
            </Table.Td>
            <Table.Td><Text size="sm">{constants.getAtlas(specimen.atlasId)?.name}</Text></Table.Td>
            <Table.Td>
                <CollectionSelect value={specimen.collection?.id} onChange={updateCollection}/>
            </Table.Td>
            <Table.Td>
                {refDataSetLabel}
                <ReferenceDatasetModal specimen={specimen} opened={isRefDataSetModalOpen} onClose={() => setIsRefDataSetModalOpen(false)}/>
            </Table.Td>
            <Table.Td>
                {tomographyLabel}
                <TomographyModal specimen={specimen} opened={isTomographyModalOpen} onClose={() => setIsTomographyModalOpen(false)}/>
            </Table.Td>
            <Table.Td>
                {features}
                <SomaFeaturesModal specimen={specimen} opened={isSomaModalOpen} onClose={() => setIsSomaModalOpen(false)}/>
            </Table.Td>
            <Table.Td>
                {specimen.neuronCount === 0 ?
                    <Button leftSection={<IconTrash size={18}/>} variant="light" color="red" children="remove" size="sm"
                            onClick={() => requestDelete(specimen)}/> :
                    <Badge p={8} variant="light">{specimen.neuronCount} {specimen.neuronCount == 1 ? "neuron" : "neurons"}</Badge>}
            </Table.Td>
        </Table.Tr>
    )
}


const InjectionList = ({injections, isExpanded}: { injections: InjectionShape[], isExpanded: boolean }) => {
    if (!injections || injections.length === 0) {
        return "(none)";
    }

    if (injections.length === 1) {
        return displayInjection(injections[0]);
    }

    if (!isExpanded) {
        return `${injections.length} injections`;
    }

    const rows = injections.map(i => (
        <List.Item key={`sil_${i.id}`}>
            {displayInjection(i)}
        </List.Item>)
    );

    return <List size="sm">{rows}</List>;
}

function PropertyRow(label: string, value: number): React.ReactNode {
    return <Group gap="sm"><Text size="sm" miw={70} c="dimmed">{label}</Text><Text size="sm">{value}</Text></Group>
}

function SomaPropertiesDisplay(somaProperties: SomaFeaturesShape) {
    const brightness = somaProperties.defaultBrightness ? PropertyRow("Brightness", somaProperties.defaultBrightness) : null;
    const volume = somaProperties.defaultVolume ? PropertyRow("Volume", somaProperties.defaultVolume) : null;

    return (
        <Stack gap={0}>
            {brightness}
            {volume}
        </Stack>
    )
}
